import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button } from '../../components/button'
import CountCard from '../../components/cards/CountCard'
import { Status } from '../../components/cards/statusTag'
import Icon from '../../components/icon'
import { DateFilterNav } from '../../components/inputs/dateFilterNav'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { CreateEventModal } from '../../components/modal/app_modals/createEventModal'
import { FeatureEventModal } from '../../components/modal/app_modals/feature-event-modal'
import { ExportDataModal } from '../../components/modal/app_modals/exportDataModal'
import { TableComponent } from '../../components/table'
import { PageHeader } from '../../components/typography/PageHeader'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import {
  formatDate,
  getEventPriceText,
  getPageFromSkip,
  getSkipFromPage,
  getVendorName,
} from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import {
  useGetAllEventsQuery,
  useLazyGetEventStatsQuery,
} from '../../redux-toolkits/events/events.slice'
import { SingleEvent } from '../../redux-toolkits/events/events.type'
import { ReportPeriodRange } from '../../redux-toolkits/reports/reports.type'
import { QueryProps } from '../../redux-toolkits/shared/shared.type'
import { FeaturedEventsPanel } from './featured-events-panel'
export const EventsPage = () => {
  const [query, setQuery] = useState<QueryProps>()
  const [selectedEvent, setSelectedEvent] = useState<SingleEvent>()
  const [eventToFeature, setEventToFeature] = useState<SingleEvent>()

  const [searchParams, setSearchParams] = useSearchParams()
  const [getEventForEdit, { isFetching: fetchingEventForEdit }] =
    useLazyGetEventStatsQuery()

  const { data: events, isFetching: fetchingEvents } = useGetAllEventsQuery(
    query
      ? {
          ...query,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const { modalOpen, modal, handleModalClose, handleModalOpen, setModalOpen } =
    useModalControl()

  useEffect(() => {
    const custDate = searchParams.get('isCustomDateRange')
      ? searchParams.get('isCustomDateRange') === 'true'
      : false

    setQuery({
      skip: Number(searchParams.get('skip') || 0),
      limit: 30,
      search: searchParams.get('search') || undefined,
      isCustomDateRange: custDate,
      filter: custDate
        ? undefined
        : (searchParams.get('filter') as ReportPeriodRange) || 'today',
      startDate: custDate
        ? searchParams.get('startDate') || undefined
        : undefined,
      endDate: custDate ? searchParams.get('endDate') || undefined : undefined,
    })
  }, [searchParams])

  return (
    <Layout>
      <div className="flex flex-col gap-10">
        <div className="flex w-full items-center gap-4 flex-wrap justify-between">
          <PageHeader title="Events" className="!mb-0" />

          <Button
            type="button"
            label="Create Event"
            disabled={fetchingEventForEdit}
            onClick={() => {
              setSelectedEvent(undefined)
              handleModalOpen('createEventModal')
            }}
            primary
            className="!w-fit"
          />
        </div>

        <div className="w-fit ml-auto">
          <DateFilterNav
            filter={query?.filter ?? 'custom'}
            isCustomDateRange={query?.isCustomDateRange ?? false}
            startDate={query?.startDate}
            endDate={query?.endDate}
            setFilter={(filter) => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                filter: filter.filter ?? '',
                isCustomDateRange: String(filter.isCustomDateRange),
                endDate: filter.endDate ?? '',
                startDate: filter.startDate ?? '',
              })
            }}
          />
        </div>

        <CountCardContainer className="!my-0">
          <CountCard
            count={events?.data?.totalItems ?? 0}
            text="Total events"
            isCurrency={false}
            loading={fetchingEvents}
          />

          <CountCard
            count={events?.data?.stats?.totalTicketsSold ?? 0}
            text="Total tickets sold"
            isCurrency={false}
            loading={fetchingEvents}
          />

          <CountCard
            count={events?.data?.stats?.totalRevenue ?? 0}
            text="Total revenue generated"
            isCurrency={true}
            loading={fetchingEvents}
          />
        </CountCardContainer>

        <FeaturedEventsPanel />

        <div className="w-full gap-4 flex flex-wrap items-center justify-between">
          <div className="!w-full md:!max-w-[400px]">
            <SearchInput
              searchFunction={(search) => {
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  search: encodeURIComponent(search),
                  skip: '0',
                })
              }}
              placeholder="Search events"
              initialValue={searchParams.get('search') || undefined}
            />
          </div>

          <Button
            label="Export"
            primary
            onClick={() => {
              handleModalOpen('exportDataModal')
            }}
          />
        </div>

        <div className="w-full overflow-x-scroll">
          <TableComponent
            loading={fetchingEvents || fetchingEventForEdit}
            headers={[
              'Event Name',
              'Creator',
              'Price',
              'Quantity',
              'Date & Time',
              'Status',
              'Actions',
            ]}
            totalDataCount={events?.data?.totalItems}
            currentPage={getPageFromSkip(query?.skip || 1, 30)}
            pageLimit={30}
            isEmpty={[undefined, 0].includes(events?.data?.data?.length)}
            onPageChange={(p) => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                skip: `${getSkipFromPage(p, 30)}`,
              })
            }}
            rows={
              events?.data?.data?.map((event) => {
                return {
                  id: event._id,
                  content: [
                    <Link
                      to={`/events/${event._id}`}
                      key={`${event._id}-name`}
                      className="hover:underline transition-all duration-300"
                    >
                      {event.name}
                    </Link>,
                    getVendorName(event.vendor),
                    getEventPriceText(
                      event.price,
                      event.eventTicketSettings?.tiers ?? [],
                    ),
                    event.inStockQuantity?.toLocaleString() ?? 'N/A',
                    formatDate(
                      event.eventTicketSettings?.eventDate,
                      'DD MMM yyyy, hh:mm a',
                    ),
                    <Status
                      text={event?.isActive ? 'Active' : 'Inactive'}
                      type={event?.isActive ? 'success' : 'fail'}
                      key={`${event._id}-status`}
                    />,
                    <Dropdown
                      className="text-left"
                      key={`${event._id}-controls`}
                      menuButton={
                        <Icon
                          id="ellipses"
                          height={18}
                          width={18}
                          className=""
                        />
                      }
                      menuItems={[
                        {
                          name: 'Edit',
                          value: 'edit',
                          onClick: async () => {
                            const response = await getEventForEdit({
                              id: event._id,
                            })
                            if (!response.data?.success) {
                              toast.error(
                                response.data?.message ??
                                  'Unable to load event for editing',
                              )
                              return
                            }
                            setSelectedEvent(response.data.data.event)
                            handleModalOpen('createEventModal')
                          },
                        },
                        {
                          name: 'Feature',
                          value: 'feature',
                          onClick: () => {
                            setEventToFeature(event)
                            handleModalOpen('featureEventModal')
                          },
                        },
                      ]}
                    />,
                  ],
                }
              }) ?? []
            }
          />
        </div>
      </div>

      <CreateEventModal
        modalOpen={modalOpen && modal === 'createEventModal'}
        handleModalClose={() => {
          handleModalClose()
          setSelectedEvent(undefined)
        }}
        setModalOpen={setModalOpen}
        mode={selectedEvent ? 'edit' : 'create'}
        onSucess={() => {
          handleModalClose()
        }}
        event={selectedEvent}
      />

      <FeatureEventModal
        open={modalOpen && modal === 'featureEventModal'}
        setOpen={setModalOpen}
        event={eventToFeature}
        onClose={() => {
          handleModalClose()
          setEventToFeature(undefined)
        }}
      />

      <ExportDataModal
        open={modalOpen && modal === 'exportDataModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={() => {
          handleModalClose()
        }}
        extraValues={{}}
        title="Export Events"
        reportType="event_summary"
      />
    </Layout>
  )
}
